import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { formatPriceWithMark } from '@this/src/util/price/price.util';
import {
  PROPOSAL_BUNDLE_CATEGORY,
  PROPOSAL_BUNDLE_CATEGORY_LABELS,
  type ProposalBundle,
  type ProposalItem
} from '../../types/proposalBundles';
import { buildFlightTimeRangeDisplay } from '../proposal/utils';
import { formatRouteDate } from '../../utils/routeInfoFormatter';
import { ForeignFlightTimeRangeCell } from '../proposal/ForeignFlightTimeRangeCell';
import StateLabel from './StateLabel';

interface Props {
  bundle: ProposalBundle;
  includeTax: boolean;
}

const hotelTotalPrice = (
  item: ProposalItem,
  stayDays: number | null
): { price: number | null; priceWithTax: number | null } | null => {
  if (!stayDays || stayDays <= 0) return null;
  const price = item.price !== null ? item.price * stayDays : null;
  const priceWithTax = item.priceWithTax !== null ? item.priceWithTax * stayDays : null;
  return { price, priceWithTax };
};

const SingleBundleCard = ({ bundle, includeTax }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const isHotel = bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOTEL;
  const checkinStr = formatRouteDate(bundle.departureDate);
  const checkoutStr = formatRouteDate(bundle.returnDate);
  const dateStr = (() => {
    if (isHotel && checkinStr && checkoutStr) return `${checkinStr}〜${checkoutStr}`;
    if (isHotel && checkinStr) return checkinStr;
    if (!isHotel && checkinStr) return checkinStr;
    return null;
  })();
  const stayDays = isHotel ? bundle.stayDays ?? null : null;
  const routeInfo = bundle.origin && bundle.destination ? `${bundle.origin} → ${bundle.destination}` : null;
  const selectedItem = bundle.items.find(item => item.selected) ?? null;
  const hotelStayDaysForItem = isHotel ? bundle.stayDays ?? selectedItem?.stayDays ?? null : null;
  const hotelTotal = selectedItem ? hotelTotalPrice(selectedItem, hotelStayDaysForItem) : null;
  const selectedItemDisplayPrice =
    selectedItem != null
      ? (() => {
          const baseFromApi = selectedItem.priceText?.trim() ?? '';
          const displayPrice = hotelTotal
            ? includeTax
              ? hotelTotal.priceWithTax
              : hotelTotal.price
            : includeTax
            ? selectedItem.priceWithTax
            : selectedItem.price;
          const base = baseFromApi || formatPriceWithMark(displayPrice);
          if (
            base != null &&
            bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD &&
            selectedItem.isAir === true
          ) {
            return baseFromApi.length > 0 ? base : `${base}（往復）`;
          }
          return base ?? '-';
        })()
      : null;

  return (
    <div style={styles.bundleCard}>
      <div style={styles.bundleHeader}>
        <span style={styles.categoryLabel}>
          {PROPOSAL_BUNDLE_CATEGORY_LABELS[bundle.category] ?? bundle.category}
        </span>
        <StateLabel state={bundle.state} style={styles.stateLabel} />
      </div>
      {(routeInfo || dateStr || (stayDays && stayDays > 0)) && (
        <div style={styles.routeInfo}>
          {routeInfo && <span>{routeInfo}</span>}
          {dateStr && <span style={styles.dateText}>（{dateStr}）</span>}
          {stayDays && stayDays > 0 && (
            <span style={styles.dateText}>
              {' '}
              / {stayDays}泊{stayDays + 1}日
            </span>
          )}
        </div>
      )}
      {selectedItem ? (
        <div style={styles.selectedItemInfo}>
          <div style={styles.selectedItemTitle}>
            {selectedItem.flightName || selectedItem.title || '-'}
            {selectedItem.seatClass && <span style={styles.seatClass}>（{selectedItem.seatClass}）</span>}
          </div>
          {selectedItem.departureTime != null && selectedItem.arrivalTime != null && (
            <div style={styles.selectedItemTime}>
              <ForeignFlightTimeRangeCell
                {...buildFlightTimeRangeDisplay(
                  selectedItem.departureTime,
                  selectedItem.arrivalTime,
                  selectedItem.departureDate,
                  selectedItem.arrivalDate
                )}
                fallbackLabel={`${selectedItem.departureTime} → ${selectedItem.arrivalTime}`}
              />
            </div>
          )}
          <div style={styles.selectedItemPrice}>{selectedItemDisplayPrice}</div>
        </div>
      ) : null}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  bundleCard: {
    padding: '10px 12px',
    backgroundColor: theme.contentBgColor,
    borderRadius: '6px',
    border: `1px solid ${theme.grayBorderColor}`
  },
  bundleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '4px'
  },
  categoryLabel: {
    fontSize: '13px',
    fontWeight: 600 as const,
    flex: 1
  },
  stateLabel: {
    fontSize: '11px',
    fontWeight: 500 as const
  },
  routeInfo: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginBottom: '4px'
  },
  dateText: {
    color: theme.grayTextColor
  },
  selectedItemInfo: {
    marginTop: '6px',
    padding: '8px',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px'
  },
  selectedItemTitle: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.textColor
  },
  seatClass: {
    fontSize: '12px',
    fontWeight: 400 as const
  },
  selectedItemTime: {
    fontSize: '12px',
    color: theme.grayTextColor,
    marginTop: '2px'
  },
  selectedItemPrice: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor,
    marginTop: '4px'
  }
});

export default SingleBundleCard;
